import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { JSONSchema7 } from "json-schema";
import * as JsonRefs from "json-refs";
import jp from "jsonpath";
import { useFormDataStore } from "../../../store/formDataStore";
import {
  getReportMetadata,
  getIncidentData,
  getEvidenceData,
} from "../../../api/reportApi";
import { VideoData } from "../../../shared/interfaces/VideoData";
import { useToast } from "../../../shared/toast";
import { rmsClient } from "../../../api/clients";
import { ReportStatus } from "../../../shared/interfaces/ReportStatus";
import { getCoComments } from "../../../api/coReviewApi";
import { useCoApprovalEnabled } from "../../../hooks/useCoApprovalEnabled";
import type { CoReviewComment } from "../../../shared/interfaces/CoReviewComment";

// Exported so other pages (e.g. ExtractedInfoPage's auto-redirect into the
// editor) gate on the exact same list — divergence causes redirect loops.
export const EDITABLE_STATUSES = [
  ReportStatus.PENDING_REVIEW,
  ReportStatus.CHANGES_REQUESTED,
];

export const useReportData = () => {
  const { reportID } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const coApprovalEnabled = useCoApprovalEnabled();

  const [videoData, setVideoData] = useState<VideoData | VideoData[] | null>(
    null,
  );
  const [videoLoadStatus, setVideoLoadStatus] = useState<
    "loading" | "loaded" | "error"
  >("loading");
  const [showRMSCommit, setShowRMSCommit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [jsonSchema, setJsonSchema] = useState<JSONSchema7 | null>(null);
  const [reportType, setReportType] = useState<string>("");
  const [lastModifiedTime, setLastModifiedTime] = useState<Date | null>(null);
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [rms, setRms] = useState<string>("");
  const [reportStatus, setReportStatus] = useState<string>("");
  const [coComments, setCoComments] = useState<CoReviewComment[]>([]);
  const [coReturnedAt, setCoReturnedAt] = useState<string | null>(null);
  const [reportOwnerUserId, setReportOwnerUserId] = useState<string | null>(null);

  const fetchExtractedIncident = useCallback(async () => {
    if (!reportID) return;

    try {
      const [metadataResult, incidentResult] = await Promise.allSettled([
        getReportMetadata(reportID),
        getIncidentData(reportID),
      ]);

      if (metadataResult.status === "fulfilled") {
        const metadata = metadataResult.value;

        if (
          !EDITABLE_STATUSES.includes(metadata.reportStatus as ReportStatus)
        ) {
          navigate(`/preview-report-info/${reportID}`);
          return;
        }

        setReportStatus(metadata.reportStatus);
        setShowRMSCommit(![null, "", "KAR"].includes(metadata.rms));
        setEnableEditing(metadata.canEdit);
        setRms(metadata.rms || "KAR");
        setCoReturnedAt(
          metadata.reportStatus === ReportStatus.CHANGES_REQUESTED
            ? (metadata.reviewedAt ?? metadata.updatedAt ?? null)
            : null,
        );
        setReportOwnerUserId(metadata.userId);

        if (!metadata.canEdit) {
          toast.warning(
            "This report is currently locked for editing because another user generated it. You can view and freely navigate through all sections, but editing is disabled. If you believe you should have access, please contact your system administrator.",
          );
        }

        if (
          coApprovalEnabled &&
          metadata.reportStatus === ReportStatus.CHANGES_REQUESTED
        ) {
          getCoComments(reportID, { cycle: "current" })
            .then(setCoComments)
            .catch((err) => {
              console.error("Error fetching CO comments:", err);
              setCoComments([]);
              toast.error(
                "An error occurred while loading CO review comments. Please refresh to try again.",
              );
            });
        } else {
          setCoComments([]);
        }

        Promise.all(metadata.evidenceIds.map((id) => getEvidenceData(id)))
          .then((evidenceResults) => {
            setVideoData(evidenceResults);
            setVideoLoadStatus("loaded");
          })
          .catch((err) => {
            console.error("Error fetching evidence data:", err);
            setVideoLoadStatus("error");
            toast.error(
              "An error occurred while fetching evidence data. Please try again later.",
            );
          });
      } else {
        throw metadataResult.reason;
      }

      if (incidentResult.status === "fulfilled") {
        const { initStore } = useFormDataStore.getState();

        setLastModifiedTime(
          incidentResult.value.updatedAt
            ? new Date(incidentResult.value.updatedAt)
            : new Date(),
        );
        initStore(incidentResult.value.reportContent, 0);
        setReportType(incidentResult.value.reportType);
      } else {
        throw incidentResult.reason;
      }
    } catch (error: any) {
      const { destroyStore } = useFormDataStore.getState();
      destroyStore();
      toast.error(
        error?.response?.data?.error ||
          "An error occurred while fetching extracted incident information. Please try again later.",
      );
      navigate("/");
    }
  }, [reportID, coApprovalEnabled]);

  const reFetchExtractedIncident = useCallback(async () => {
    if (!reportID) return;

    try {
      const [metadataResult, incidentResult] = await Promise.allSettled([
        getReportMetadata(reportID),
        getIncidentData(reportID),
      ]);

      if (metadataResult.status === "fulfilled") {
        const metadata = metadataResult.value;
        if (
          !EDITABLE_STATUSES.includes(metadata.reportStatus as ReportStatus)
        ) {
          navigate(`/preview-report-info/${reportID}`);
          return;
        }

        // Refresh CO-review state so owner/resolve/finalize gating reflects
        // the latest metadata (e.g. after a websocket-triggered refresh).
        setReportStatus(metadata.reportStatus);
        setCoReturnedAt(
          metadata.reportStatus === ReportStatus.CHANGES_REQUESTED
            ? (metadata.reviewedAt ?? metadata.updatedAt ?? null)
            : null,
        );
        setReportOwnerUserId(metadata.userId);

        if (
          coApprovalEnabled &&
          metadata.reportStatus === ReportStatus.CHANGES_REQUESTED
        ) {
          getCoComments(reportID, { cycle: "current" })
            .then(setCoComments)
            .catch((err) => {
              console.error("Error fetching CO comments:", err);
              setCoComments([]);
              toast.error(
                "An error occurred while loading CO review comments. Please refresh to try again.",
              );
            });
        } else {
          setCoComments([]);
        }
      } else {
        throw metadataResult.reason;
      }

      if (incidentResult.status === "fulfilled") {
        const { setFormData } = useFormDataStore.getState();
        setLastModifiedTime(
          incidentResult.value.updatedAt
            ? new Date(incidentResult.value.updatedAt)
            : new Date(),
        );
        setFormData(incidentResult.value.reportContent);
        setReportType(incidentResult.value.reportType);
      } else {
        throw incidentResult.reason;
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          "An error occurred while fetching extracted incident information. Please try again later.",
      );
      navigate("/");
    }
  }, [reportID, navigate, toast, coApprovalEnabled]);

  useEffect(() => {
    fetchExtractedIncident();
  }, [fetchExtractedIncident]);

  useEffect(() => {
    if (!reportType) return;
    rmsClient
      .get(`/schema?reportType=${reportType}`)
      .then((response) =>
        JsonRefs.resolveRefs(response.data.schema as JSONSchema7),
      )
      .then((response) => {
        const resolvedSchema = response.resolved;
        jp.query(resolvedSchema, "$..[?(@.oneOf)]").forEach((property: any) => {
          property.enum = property.oneOf.map((option: any) => option.const);
        });
        setJsonSchema(resolvedSchema as JSONSchema7);
        setLoading(false);
      })
      .catch((error) => {
        // Without this catch a failed schema fetch left `loading` true forever
        // and the edit page showed its skeleton indefinitely. Mirror the
        // hook's existing fatal-error behavior (fetchExtractedIncident's
        // catch): return home, with an error toast.
        console.error("Failed to load report schema:", error);
        toast.error(
          error?.response?.data?.error ||
            "An error occurred while loading the report form. Please try again later.",
        );
        navigate("/");
      });
  }, [reportType]);

  return {
    reportID,
    videoData,
    setVideoData,
    videoLoadStatus,
    showRMSCommit,
    setShowRMSCommit,
    loading,
    setLoading,
    jsonSchema,
    reportType,
    setReportType,
    lastModifiedTime,
    setLastModifiedTime,
    enableEditing,
    setEnableEditing,
    reFetchExtractedIncident,
    reportRms: rms,
    reportStatus,
    coComments,
    setCoComments,
    coReturnedAt,
    reportOwnerUserId,
  };
};
